import { rateLimiterMaxBacklogSize } from '../../common-constants';
import { RateLimiterScopes } from '../../enums/ratelimiter-scopes';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';

/**
 * Configuration for a RateLimiter task — throttles flow throughput by enforcing
 * execution limits per time window (P/S, P/M, P/H, or custom multi-window).
 * Executions exceeding the limit wait in a backlog and resume once a slot opens, except for sync
 * executions, which fail at the limit. Supports optional daily caps and backlog warning thresholds.
 */
export class RateLimiterConfig extends DestinationTaskConfigBase {
	/**
	 * Master switch for the rate limit: the fixed-category limit (timedLimit + limitCategory)
	 * and the custom tiers (customLimitRates) are only enforced when this is true.
	 * The daily limit is controlled separately by dailyLimitEnable.
	 * @default false
	 */
	rateLimitEnable = false;

	/**
	 * Enables a separate daily execution cap in addition to the window limit.
	 * @default false
	 */
	dailyLimitEnable = false;

	/**
	 * Maximum executions allowed within the limitCategory window. Only enforced
	 * when rateLimitEnable=true.
	 * @default 0
	 */
	timedLimit = 0;

	/**
	 * Time window category for the fixed rate limit.
	 * 'P/S' = per second, 'P/M' = per minute, 'P/H' = per hour,
	 * 'custom' = use customLimitRates instead of timedLimit.
	 * @default 'P/S'
	 */
	limitCategory = 'P/S';

	/**
	 * Maximum total executions allowed per calendar day. Only used when dailyLimitEnable=true.
	 * @default 0
	 */
	dailyLimit = 0;

	/**
	 * Use `overflowThreshold` as the most executions that may wait in the backlog, and notify when it
	 * overflows. When false the backlog holds up to `rateLimiterMaxBacklogSize` executions, which is what the
	 * queue can hold, and a full backlog is not notified.
	 * TODO: Only evaluated, the notification is not sent yet. Sending it depends on a separate
	 * feature that is still in progress.
	 * @default false
	 */
	overflowThresholdEnabled = false;

	/**
	 * The backlog length at which the backlog overflows, when `overflowThresholdEnabled` is true. An
	 * execution that arrives when the backlog is this long fails with the rate limit as the reason. Never
	 * above `rateLimiterMaxBacklogSize`.
	 * @default rateLimiterMaxBacklogSize
	 */
	overflowThreshold = rateLimiterMaxBacklogSize;

	/**
	 * Warn when the backlog reaches `warningThreshold` executions.
	 * TODO: Only evaluated, the notification is not sent yet. Sending it depends on a separate
	 * feature that is still in progress.
	 * @default false
	 */
	warningThresholdEnabled = false;

	/**
	 * Backlog length at which the warning applies, when `warningThresholdEnabled` is true.
	 * @default 0
	 */
	warningThreshold = 0;

	/**
	 * Multi-window custom rate limit configuration. Only used when limitCategory='custom'.
	 * Allows simultaneous per-second, per-minute, and per-hour limits evaluated independently.
	 */
	customLimitRates: {
		seconds: {
			limit: number;
			seconds: number;
			enabled: boolean;
		};
		minutes: {
			limit: number;
			minutes: number;
			enabled: boolean;
		};
		hours: {
			limit: number;
			hours: number;
			enabled: boolean;
		};
	} = {
		seconds: {
			limit: 0,
			seconds: 1,
			enabled: false,
		},
		minutes: {
			limit: 0,
			minutes: 1,
			enabled: false,
		},
		hours: {
			limit: 0,
			hours: 1,
			enabled: false,
		},
	};

	/**
	 * Scope at which the rate limit counter is shared.
	 * Flow=0 (per this flow), Project=1 (project-wide, scopeKey required),
	 * Client=2 (client-space-wide, scopeKey required).
	 * @default RateLimiterScopes.Flow
	 */
	scope?: RateLimiterScopes = RateLimiterScopes.Flow;

	/**
	 * Custom scope key for Project or Client scope. Required when scope is Project or Client
	 * and a clientId/projectId is set — the service throws if absent in those cases.
	 */
	scopeKey?: string;

	constructor(init?: Partial<RateLimiterConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
